import ArrowRight from '@/assets/icons/arrow-right-solid.svg'
import SuitcaseIcon from '@/assets/icons/suitcase.svg'
import TaxiIcon from '@/assets/icons/taxi.svg'
import TicketIcon from '@/assets/icons/ticket.svg'
import AppStoreImage from '@/assets/imgs/app-store.svg'
import MobileAppImage from '@/assets/imgs/mobile-app.webp'
import PlayStoreImage from '@/assets/imgs/play-store.svg'
import { cn } from '@/core/lib/utils/cn'
import { getAppSettings } from '@/features/content/providers/server'

import { getTranslations } from 'next-intl/server'
import Image from 'next/image'
import { ReactNode } from 'react'

type InviteItemProps = {
  icon: ReactNode
  borderTop?: boolean
  borderRight?: boolean
  borderLeft?: boolean
  borderBottom?: boolean
  arrow?: 'left' | 'right' | 'down' | 'none'
  text?: string
}
const InviteItem: React.FC<InviteItemProps> = ({
  icon,
  borderBottom,
  borderLeft,
  borderRight,
  borderTop,
  arrow = 'none',
  text,
}) => {
  return (
    <div className='relative flex flex-col items-center gap-3'>
      <div className='flex flex-row items-center'>
        <div
          className={cn(
            '-rotate-45 rounded-full border-2 border-solid p-2 outline-2 outline-transparent',
            borderBottom ? 'border-b-sky-blue' : 'border-b-transparent',
            borderLeft ? 'border-l-sky-blue' : 'border-l-transparent',
            borderRight ? 'border-r-sky-blue' : 'border-r-transparent',
            borderTop ? 'border-t-sky-blue' : 'border-t-transparent',
          )}
        >
          <div className='text-sky-blue flex h-14 w-14 rotate-45 items-center justify-center rounded-full bg-white p-2'>
            {icon}
          </div>
        </div>
        {arrow !== 'none' && (
          <div
            className={cn(
              'flex items-center',
              arrow === 'down' &&
                'translate-x-0.5 -translate-y-0.5 flex-col items-center justify-center',
              arrow === 'right' && 'rotate-180',
            )}
          >
            <ArrowRight
              className={`w-10 -translate-x-0.5 ${arrow === 'down' && 'h-10 rotate-90'}`}
            />
            <div
              className={cn(
                'border-sky-blue w-10 border-[1px] border-dashed',
                arrow === 'down' &&
                  'h-10 w-auto -translate-x-0.5 translate-y-0.5',
              )}
            ></div>
          </div>
        )}
      </div>
      <p
        className={cn(
          'text-navy static left-0 mx-2 w-min max-w-20 self-start text-sm font-semibold',
          arrow === 'down' && '-translate-y-full',
        )}
      >
        {text}
      </p>
    </div>
  )
}

const Invite = async () => {
  const t = await getTranslations()
  return (
    <>
      <div className='text-sky-blue flex flex-col items-center gap-4 rounded-lg p-4 sm:hidden'>
        <div className='flex items-center'>
          <InviteItem
            arrow='left'
            borderTop
            borderRight
            borderLeft
            icon={<TaxiIcon className='bg-white' />}
            text={t('landing.easyRide')}
          />
          <InviteItem
            arrow='none'
            borderTop
            borderRight
            borderBottom
            icon={<TicketIcon className='bg-white' />}
            text={t('landing.simplePayment')}
          />
        </div>
        <div className='flex items-center'>
          <InviteItem
            arrow='left'
            borderTop
            borderRight
            borderLeft
            icon={<SuitcaseIcon className='bg-white' />}
            text={t('landing.confidentJourney')}
          />
          <InviteItem
            borderTop
            borderRight
            borderBottom
            icon={<TaxiIcon className='bg-white' />}
            text={t('landing.safeArrival')}
          />
        </div>
      </div>
      <div className='hidden items-center rounded-lg p-4 sm:flex'>
        <InviteItem
          arrow='left'
          borderTop
          borderRight
          borderLeft
          icon={<TaxiIcon className='bg-white' />}
          text={t('landing.easyRide')}
        />
        <InviteItem
          arrow='left'
          borderTop
          borderRight
          icon={<TicketIcon className='bg-white' />}
          text={t('landing.simplePayment')}
        />
        <InviteItem
          arrow='left'
          borderTop
          borderRight
          icon={<SuitcaseIcon className='bg-white' />}
          text={t('landing.confidentJourney')}
        />
        <InviteItem
          borderTop
          borderRight
          borderBottom
          icon={<TaxiIcon className='bg-white' />}
          text={t('landing.safeArrival')}
        />
      </div>
    </>
  )
}

type Props = {
  className?: string
}
export const MobileApp: React.FC<Props> = async ({ className = '' }) => {
  const t = await getTranslations()
  const appSettings = await getAppSettings()
  return (
    <section
      className={cn(
        'bg-pale-blue flex flex-col-reverse items-center gap-10 rounded-xl md:px-4 xl:flex-row xl:gap-16',
        className,
      )}
    >
      <Image
        src={MobileAppImage}
        alt='Download mobile app'
        width={400}
        height={400}
        className='block xl:pt-10'
      />

      <div className='my-4 flex flex-1 flex-col justify-center gap-8'>
        <div className='flex items-center justify-center'>
          <Invite />
        </div>
        <div className='flex flex-col items-center justify-center gap-2 md:flex-row'>
          <a
            href={appSettings?.generalInfo.appStoreDownloadUrl}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t('ariaLabels.downloadDriverIos')}
          >
            <AppStoreImage aria-hidden='true' />
          </a>
          <a
            href={appSettings?.generalInfo.googlePlayDownloadUrl}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t('ariaLabels.downloadDriverAndroid')}
          >
            <PlayStoreImage aria-hidden='true' />
          </a>
        </div>
      </div>
    </section>
  )
}
